import type { PartDefinition } from "@rogue/rackbuilder"
import { FTPowerRackBuilder } from "../../../powerRack/ftPowerRackBuilder"
import { FTMaterialBuilder } from "../../materials/ftMaterialBuilder"
import { LRLPContext, createTopBeam, createBackTopCrossmember, createBackBottomCrossmember, createStringerBracket, createStringer, createRMLBolt, createRMBolt, createRoller } from "./lrlpParts"
import { RackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
import { getRackModel } from "@/js/pages/product/rackbuilder/modelPaths"

export type LRLPComponents = {
  topBeam: PartDefinition
  backTopCrossmember: PartDefinition
  backBottomCrossmember: PartDefinition
  stringerBrackets?: {
    top: PartDefinition
    bottom: PartDefinition
  }
}

// MARK: Builder
export class LatAndLowRowBuilder {
  private config: RackOptions
  private context: LRLPContext

  constructor(config: RackOptions) {
    this.config = config
    this.context = this.createContext(config)
  }

  // MARK: Context Factory
  private createContext(config: RackOptions): LRLPContext {
    const materialBuilder = new FTMaterialBuilder(config)
    
    return {
      config,
      materials    : materialBuilder.createMainMaterial(),
      fxtMaterials : {
        main     : { name: 'metal', material: 'base' },
        hardware : { material: config.hardware },
      },
      
      // Extracted config
      line                 : config.line,
      height               : config.height,
      depth                : config.depth,
      isRM                 : config.line === 'RM',
      isRML                : config.line === 'RML',
      isHalfRack           : config.halfRack ?? false,
      isSlimRack           : config.latAndLowRowOptions?.isSlimRack ?? false,
      areStringersSelected : !!config.latAndLowRowOptions?.stringers,
      weightType           : config.latAndLowRowOptions?.resistanceType?.includes('250') ? '250' : '300',
    }
  }

  // MARK: Build - Factory use
  public build(): PartDefinition {
    const baseRack = new FTPowerRackBuilder(this.config).build()

    // 4-post addon: the front crossmember ('crossmember top 4') is part of the product
    // so its body should be solid, but the bolts (which attach to the existing rack)
    // are not included — same treatment as the back top crossmember.
    if (!this.config.sixPost && this.config.latAndLowRowOptions?.isAddon) {
      const frontCrossmember = baseRack.slots.top as PartDefinition
      if (frontCrossmember) {
        frontCrossmember.materials = {
          ...this.context.fxtMaterials,
          hardware: { config: { opacity: 0.2 } },
        }
      }
    }

    this.attachToSide(baseRack, true)
    this.attachToSide(baseRack, false)

    // @TODO: Band pegs on the LRLP currently only show one peg due to positioning.
    // Default positions (z:-2 front, z:2 back) may need adjustment for the LRLP
    // crossmember system. Confirm with business whether band pegs are intentional
    // on this product before implementing repositioning.

    return baseRack
  }

  // MARK: For external attachment only (The Cave 2.0)
  public createComponents(left: boolean): LRLPComponents {
    const topBeam = createTopBeam(this.context)

    const components: LRLPComponents = {
      topBeam,
      backTopCrossmember    : createBackTopCrossmember(this.context, topBeam),
      backBottomCrossmember : createBackBottomCrossmember(this.context),
    }

    // 4-post addon: back top crossmember bolts are not included with the product.
    // Spread into a new object to avoid mutating the shared fxtMaterials reference
    // which would also apply opacity to backBottomCrossmember.
    if (!this.config.sixPost && this.config.latAndLowRowOptions?.isAddon) {
      components.backTopCrossmember.materials = {
        ...components.backTopCrossmember.materials,
        hardware: { config: { opacity: 0.2 } },
      }
    }

    if (this.context.isSlimRack) {
      const { topOffset, bottomOffset } = this.getStringerBracketOffsets(left)
      components.stringerBrackets = {
        top    : createStringerBracket(topOffset, left),
        bottom : createStringerBracket(bottomOffset, left),
      }
    }

    return components
  }

  // MARK: Attachment Logic
  private attachToSide(rack: PartDefinition, left: boolean): void {
    const frontUpright = this.getFrontUpright(rack, left)
    const topCrossmember = this.getTopCrossmember(rack, left)
    const bottomCrossmember = this.getBottomCrossmember(rack, left)
    const backUpright = this.getBackUpright(rack, left)

    // Apply slim rack crossmember modifications
    this.applySlimRackModifications(topCrossmember, bottomCrossmember, frontUpright, left)

    // Only add weight stack system on left side
    if (!left) return

    // Create and attach components to standard slots
    const components = this.createComponents(left)

    if (this.context.isHalfRack) {
      backUpright.slots.top = components.backTopCrossmember
    } else {
      backUpright.slots['crossmember-top'] = components.backTopCrossmember
    }

    backUpright.slots['crossmember-bottom'] = components.backBottomCrossmember

    this.addRoller(frontUpright, bottomCrossmember)
  }

  // MARK: Slim Rack Modifications
  public applySlimRackModifications(
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    left: boolean
  ): void {
    if (!this.context.isSlimRack) return

    const { line, isRM, isRML, fxtMaterials, areStringersSelected } = this.context

    // Change crossmembers to custom 14"
    topCrossmember.mesh = getRackModel(`${line}_LRLP`,isRM ? 'rm crossmember top 1' : 'rml crossmember 1')

    bottomCrossmember.mesh = getRackModel(`${line}_LRLP`, isRM ? 'rm crossmember bottom 1' : 'rml crossmember 1')

    topCrossmember.materials = fxtMaterials
    bottomCrossmember.materials = fxtMaterials

    // Side-specific offsets
    if (!left && isRM) {
      topCrossmember.offset = { x: 0.6 }
    } else if (!left && isRML) {
      frontUpright.offset = { x: 0.1 }
    }

    // Add stringer brackets
    const { topOffset, bottomOffset } = this.getStringerBracketOffsets(left)
    const topStringerBracket = createStringerBracket(topOffset, left)
    const bottomStringerBracket = createStringerBracket(bottomOffset, left)

    bottomCrossmember.slots.last = bottomStringerBracket
    topCrossmember.slots.back = topStringerBracket

    // Add bolts
    this.attachStringerBolts(topStringerBracket, bottomStringerBracket, left)

    // Add stringer if selected
    if (areStringersSelected && left) {
      const stringer = createStringer()
      topStringerBracket.slots.left = stringer
      bottomStringerBracket.slots.left = stringer
    }
  }

  // MARK: Stringer Bolt Attachment
  private attachStringerBolts(
    topBracket: PartDefinition,
    bottomBracket: PartDefinition,
    left: boolean
  ): void {
    if (this.context.isRML) {
      this.attachRMLBolts(topBracket, bottomBracket, left)
    }

    if (this.context.isRM) {
      this.attachRMBolts(topBracket, bottomBracket)
    }
  }

  private attachRMLBolts(
    topBracket: PartDefinition,
    bottomBracket: PartDefinition,
    left: boolean
  ): void {
    if (left) {
      bottomBracket.slots.top_bolt = createRMLBolt()
      bottomBracket.slots.bottom_bolt = createRMLBolt()
      topBracket.slots.bottom_bolt = createRMLBolt({ y: -0.3 })
      topBracket.slots.top_bolt = createRMLBolt({ y: -0.3 })
    } else {
      bottomBracket.slots.bottom_bolt = createRMLBolt({ y: -0.8 })
      bottomBracket.slots.top_bolt = createRMLBolt({ y: -0.8 })
      topBracket.slots.top_bolt = createRMLBolt({ y: -0.4 })
      topBracket.slots.bottom_bolt = createRMLBolt({ y: -0.4 })
    }
  }

  private attachRMBolts(topBracket: PartDefinition, bottomBracket: PartDefinition): void {
    topBracket.slots.rm_top_bolt = createRMBolt({ z: 0.35 })
    topBracket.slots.rm_bottom_bolt = createRMBolt({ z: 0.35 })
    bottomBracket.slots.rm_top_bolt = createRMBolt({ z: 0.45 })
    bottomBracket.slots.rm_bottom_bolt = createRMBolt({ z: 0.45 })
  }

  // MARK: Stringer Bracket Offsets
  private getStringerBracketOffsets(left: boolean): { topOffset: object; bottomOffset: object } {
    const { isRM, isRML } = this.context

    if (isRM && left) {
      return {
        topOffset    : { z: -1.1, y: -11.7 },
        bottomOffset : { z: -5, y: 9.5 },
      }
    }
    
    if (isRM && !left) {
      return {
        topOffset    : { z: -1.1, x: -0.5, y: -10.4 },
        bottomOffset : { z: -5, y: 10.7 },
      }
    }
    
    if (isRML && left) {
      return {
        topOffset    : { z: -1.4, y: -11.7 },
        bottomOffset : { z: -5.8, y: 8.2 },
      }
    }
    
    // isRML && !left
    return {
      topOffset    : { z: -1.4, x: 0, y: -10.4 },
      bottomOffset : { z: -5.8, y: 9.4 },
    }
  }

  // MARK: Roller
  public addRoller(frontUpright: PartDefinition, bottomCrossmember: PartDefinition): void {
    if (!this.config.rackRoller) return

    const uprightForRoller = !this.config.sixPost
      ? frontUpright
      : (bottomCrossmember.slots.back as PartDefinition)

    uprightForRoller.slots['safety-low'] = createRoller()
  }

  // MARK: Navigation Helpers
  private getFrontUpright(rack: PartDefinition, left: boolean): PartDefinition {
    return left ? rack : rack.slots['other-side'] as PartDefinition
  }

  private getTopCrossmember(rack: PartDefinition, left: boolean): PartDefinition {
    const side = this.getFrontUpright(rack, left)
    const crossmembers = side.slots['crossmember-top'] as PartDefinition | PartDefinition[]
    
    return Array.isArray(crossmembers) ? crossmembers[0] : crossmembers
  }

  private getBottomCrossmember(rack: PartDefinition, left: boolean): PartDefinition {
    const side = this.getFrontUpright(rack, left)
    const crossmembers = side.slots['crossmember-bottom'] as PartDefinition | PartDefinition[]
    
    return Array.isArray(crossmembers) ? crossmembers[0] : crossmembers
  }

  private getBackUpright(rack: PartDefinition, left: boolean): PartDefinition {
    const crossmember = this.getBottomCrossmember(rack, left)
    const middleUpright = crossmember.slots.back as PartDefinition

    // On 6-post (storage) racks, the LRLP system belongs on the storage back post,
    // not the middle post. Traversing one level deeper prevents overwriting the
    // storage crossmember connection and places components in the correct position.
    if (this.config.sixPost) {
      const storageCrossmember = middleUpright.slots['crossmember-bottom'] as PartDefinition
      
      return storageCrossmember.slots.back as PartDefinition
    }

    return middleUpright
  }
}